import { type FC, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Box,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useCurrentPlatformQuery,
  usePlatformFeature,
  useUpdateCurrentPlatformMutation,
} from "@/partner/entities/platforms";
import { useUser } from "@/partner/entities/auth";
import { FormBuilder } from "@/shared/components/Form";
import { ErrorMessage } from "@/shared/components";
import {
  BunnySettingsCard,
  StreamingSettingsCard,
} from "@/partner/features/streamingSettings";
import {
  PaymentSettingsCard,
  PaynetSettingsCard,
  CCBillSettingsCard,
  PayPalSettingsCard,
} from "@/partner/features/paymentSettings";
import { DomainSettingsCard } from "@/partner/features/domainSettings";
import { LiveReactionsSettingsCard } from "@/partner/features/liveReactionsSettings";
import { MarketingSettingsCard } from "@/partner/features/marketingSettings";
import { ViewingSettingsCard } from "@/partner/features/viewingSettings";
import { StreamSignalSettingsCard } from "@/partner/features/streamSignalSettings";
import { useInstancePaymentProviders } from "@/partner/entities/instance";
import {
  PaymentProviderType,
  PlatformFeatureType,
} from "@/types/graphql/partner.generated";
import {
  makePlatformSettingsSchema,
  makePlatformSettingsValidationSchema,
  mapPlatformToFormValues,
} from "../model";
import { validateWithZod } from "@/shared/utils/zod";

export const PlatformSettings: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const { data, loading, error, refetch } = useCurrentPlatformQuery();
  // Карточки подключения только тех провайдеров, что включены на
  // развёртывании (`instance.paymentProviders`): на ottkit.io это Stripe и
  // Paynet, на adult-инстансе только CCBill. Подключение остальных
  // бэкенд всё равно отклоняет (PaymentSettingsService).
  const paymentProviders = useInstancePaymentProviders();
  const isProviderEnabled = (provider: PaymentProviderType) =>
    Boolean(paymentProviders?.includes(provider));
  // Блоки за фичефлагами платформы, которые включает админ.
  const liveReactionsFeature = usePlatformFeature(
    PlatformFeatureType.LiveReactionsType
  );
  const marketingEmailsFeature = usePlatformFeature(
    PlatformFeatureType.MarketingEmailsType
  );

  const [updateCurrentPlatform, { loading: updating }] =
    useUpdateCurrentPlatformMutation({
      onError: (error) => {
        enqueueSnackbar(error.message, {
          variant: "error",
        });
      },
      onCompleted: () => {
        enqueueSnackbar(t("platformSettings.snackbar.updated"), {
          variant: "success",
        });
        refetch();
      },
    });

  const reportCurrency = user.currentOrganization?.currency;
  const schema = useMemo(
    () => makePlatformSettingsSchema(t, reportCurrency),
    [t, reportCurrency]
  );
  const validate = useMemo(
    () => validateWithZod(makePlatformSettingsValidationSchema(t)),
    [t]
  );

  const initialValues = useMemo(
    () => mapPlatformToFormValues(data?.currentPlatform),
    [data]
  );

  const handleSubmit = async (values: Record<string, any>) => {
    await updateCurrentPlatform({
      variables: {
        input: values,
      },
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  if (!data?.currentPlatform) {
    return (
      <Card>
        <CardContent>
          <ErrorMessage error={new Error(t("platform.errors.noPlatform"))} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader
          title={t("platformSettings.card.title")}
          sx={{ px: 2, pt: 2, pb: 0 }}
        />
        <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
          <FormBuilder
            formId="platform-settings-form"
            schema={schema}
            validate={validate}
            initialValues={initialValues}
            editData={data.currentPlatform}
            onSubmit={handleSubmit}
            submitButton
            resetButton={false}
            submitButtonText={t("common.actions.save")}
            disabled={updating}
            spacing={3}
            actionsAlign="end"
          />
        </CardContent>
      </Card>
      <ViewingSettingsCard />
      <StreamSignalSettingsCard />
      {liveReactionsFeature && <LiveReactionsSettingsCard />}
      {marketingEmailsFeature && <MarketingSettingsCard />}
      {data?.currentPlatform?.effectiveVideoProvider === "BUNNY" ? (
        <BunnySettingsCard />
      ) : (
        <StreamingSettingsCard />
      )}
      <DomainSettingsCard />
      {isProviderEnabled(PaymentProviderType.StripeType) && (
        <PaymentSettingsCard />
      )}
      {isProviderEnabled(PaymentProviderType.PaynetType) && (
        <PaynetSettingsCard />
      )}
      {isProviderEnabled(PaymentProviderType.CcbillType) && (
        <CCBillSettingsCard />
      )}
      {isProviderEnabled(PaymentProviderType.PaypalType) && (
        <PayPalSettingsCard />
      )}
    </>
  );
};
